import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SearchSelectField } from '@/components/ui/SearchSelectField/SearchSelectField';
import { api } from '@/api/client';
import { useCanManageCatalog } from '@/contexts/CatalogManageOpenerContext';

const PAGE_SIZE = 25;
const MIN_SEARCH_LENGTH = 2;

export type DepartmentValueItem = { id: string; code: string };

interface DepartmentComboProps {
  value: DepartmentValueItem | null;
  onChange: (v: DepartmentValueItem | null) => void;
  placeholder?: string;
  disabled?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  onCreateError?: (err: Error) => void;
  debounceFlushKey?: string | number;
}

export function DepartmentCombo({
  value,
  onChange,
  placeholder = 'Подразделение',
  disabled,
  containerRef,
  onCreateError,
  debounceFlushKey,
}: DepartmentComboProps) {
  const canManageCatalog = useCanManageCatalog();
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: (code: string) => api.departments.create({ code }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      onChange({ id: created.id, code: created.code });
    },
    onError: (err: Error) => onCreateError?.(err),
  });

  return (
    <SearchSelectField<DepartmentValueItem>
      value={value}
      onChange={onChange}
      getOptionLabel={(d) => d.code}
      placeholder={placeholder}
      disabled={disabled}
      containerRef={containerRef}
      queryKey={['departments', 'search-field']}
      normalizeQuery={(raw) => raw.replace(/\D/g, '').slice(0, 3)}
      fetchPage={(debouncedQuery, page) =>
        api.departments.list(debouncedQuery, { limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      }
      minSearchLength={MIN_SEARCH_LENGTH}
      debounceFlushKey={debounceFlushKey}
      computeAddons={({ debouncedQuery, filteredItems }) => {
        if (!canManageCatalog) return [];
        const q = debouncedQuery.replace(/\D/g, '');
        if (q.length !== 3) return [];
        const exact = filteredItems.some((r) => r.code === q);
        if (exact || createMutation.isPending) return [];
        return [
          {
            id: '__create_dept',
            label: `+ Добавить «${q}»`,
            onSelect: () => createMutation.mutate(q),
          },
        ];
      }}
      addonPending={createMutation.isPending}
    />
  );
}
